<template>
  <div
    ref="root"
    class="label-dropdown"
    :class="[
      {
        'label-dropdown--open': isOpen,
        'label-dropdown--error': hasError,
        'label-dropdown--flex': flex,
        'label-dropdown--inline': inline,
        'label-dropdown--no-label': hideLabel,
      },
      customClass,
    ]"
  >
    <span v-if="!hideLabel" :id="labelId" class="label-dropdown__label">
      {{ label }}<span v-if="required" class="label-dropdown__required" aria-hidden="true">*</span>
    </span>
    <div
      class="label-dropdown__control"
      :class="{ 'label-dropdown__control--wide': wide }"
    >
      <button
        :id="selectId"
        type="button"
        class="label-dropdown__trigger"
        :class="{ 'label-dropdown__trigger--open': isOpen }"
        :disabled="disabled"
        role="combobox"
        aria-haspopup="listbox"
        :aria-expanded="isOpen ? 'true' : 'false'"
        :aria-controls="listboxId"
        :aria-labelledby="hideLabel ? undefined : labelId"
        :aria-label="hideLabel ? resolvedAriaLabel : undefined"
        @click="toggleOpen"
        @keydown="onTriggerKeydown"
      >
        <span class="label-dropdown__value-wrap">
          <span
            v-if="selectedOptionIcon"
            class="label-dropdown__option-icon"
            :class="'label-dropdown__option-icon--' + selectedOptionTone"
            aria-hidden="true"
          >
            <i :class="selectedOptionIcon"></i>
          </span>
          <span class="label-dropdown__value">{{ displayLabel }}</span>
          <span
            v-if="selectedOptionDetail && !selectedOptionDetailInline"
            class="label-dropdown__option-detail label-dropdown__trigger-detail"
            :class="selectedOptionDetailClasses"
          >{{ selectedOptionDetail }}</span>
        </span>
        <i class="bi bi-chevron-down label-dropdown__chevron" aria-hidden="true"></i>
      </button>
    </div>

    <div v-if="$slots.default" class="label-dropdown__messages">
      <slot />
    </div>

    <Teleport to="body">
      <Transition name="label-dropdown-menu">
        <div
          v-if="isOpen"
          ref="menuPanel"
          :id="listboxId"
          class="label-dropdown__menu-panel label-dropdown__menu-panel--teleported"
          :class="menuClass"
          :style="menuStyle"
          role="presentation"
        >
          <div
            v-if="showSearch"
            class="label-dropdown__search-wrap"
            @mousedown.stop
          >
            <i class="bi bi-search label-dropdown__search-icon" aria-hidden="true"></i>
            <input
              ref="searchInput"
              type="search"
              class="label-dropdown__search"
              :placeholder="searchPlaceholder"
              :aria-label="`Search ${label}`"
              autocomplete="off"
              autocorrect="off"
              spellcheck="false"
              :value="searchQuery"
              @input="onSearchInput"
              @keydown="onSearchKeydown"
            />
            <button
              v-if="searchQuery"
              type="button"
              class="label-dropdown__search-clear"
              aria-label="Clear search"
              tabindex="-1"
              @mousedown.prevent
              @click="clearSearch"
            >
              <i class="bi bi-x" aria-hidden="true"></i>
            </button>
          </div>

          <ul
            class="label-dropdown__menu"
            role="listbox"
            :aria-labelledby="hideLabel ? undefined : labelId"
            :aria-label="hideLabel ? resolvedAriaLabel : undefined"
          >
            <li
              v-for="(option, index) in filteredOptions"
              :key="String(option.value) + '-' + index"
              role="option"
              class="label-dropdown__option"
              :class="{
                'label-dropdown__option--selected': isSelected(option),
                'label-dropdown__option--active': activeIndex === index,
                'label-dropdown__option--disabled': option.disabled,
                'label-dropdown__option--group': option.groupHeader,
              }"
              :aria-selected="isSelected(option) ? 'true' : 'false'"
              :aria-disabled="option.disabled ? 'true' : 'false'"
              @mousedown.prevent="selectOption(option)"
              @mouseenter="activeIndex = index"
            >
              <span class="label-dropdown__option-content">
                <span
                  v-if="option.icon"
                  class="label-dropdown__option-icon"
                  :class="'label-dropdown__option-icon--' + (option.tone || 'other')"
                  aria-hidden="true"
                >
                  <i :class="option.icon"></i>
                </span>
                <span class="label-dropdown__option-main">{{ option.label }}</span>
              </span>
              <span
                v-if="option.detail"
                class="label-dropdown__option-detail"
                :class="optionDetailClasses(option)"
              >{{ option.detail }}</span>
            </li>
            <li
              v-if="showCreateFromSearch"
              role="option"
              class="label-dropdown__option label-dropdown__option--create"
              :class="{ 'label-dropdown__option--active': activeIndex === filteredOptions.length }"
              @mousedown.prevent="submitCreate(searchQuery.trim())"
              @mouseenter="activeIndex = filteredOptions.length"
            >
              <i class="bi bi-plus-lg" aria-hidden="true"></i>
              Add "{{ searchQuery.trim() }}"
            </li>
            <li
              v-else-if="showNoResults"
              class="label-dropdown__option label-dropdown__option--empty"
              role="presentation"
            >
              {{ noResultsText }}
            </li>
          </ul>

          <div
            v-if="allowCreate"
            class="label-dropdown__create"
            @mousedown.stop
          >
            <input
              ref="createInput"
              v-model="createName"
              type="text"
              class="label-dropdown__create-input"
              :placeholder="resolvedCreatePlaceholder"
              :disabled="creating || disabled"
              autocomplete="off"
              @keydown.enter.prevent="submitCreate(createName.trim())"
            />
            <button
              type="button"
              class="label-dropdown__create-btn"
              :disabled="creating || disabled || !createNameTrimmed"
              :title="`Add ${label}`"
              @click="submitCreate(createNameTrimmed)"
            >
              <i class="bi bi-plus-lg" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: 'LabelDropdown',
  props: {
    label: {
      type: String,
      default: '',
    },
    /** When true, hides the visible label (use `label` or `ariaLabel` for accessibility). */
    hideLabel: {
      type: Boolean,
      default: false,
    },
    ariaLabel: {
      type: String,
      default: '',
    },
    selectId: {
      type: String,
      required: true,
    },
    modelValue: {
      type: [String, Number],
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
    },
    /** @deprecated No longer changes width; dropdowns are fluid by default. */
    wide: {
      type: Boolean,
      default: false,
    },
    /** Fill the parent form column (use in grid/flex rows). */
    flex: {
      type: Boolean,
      default: false,
    },
    /** Fixed intrinsic width for filter toolbars (not full-width forms). */
    inline: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    customClass: {
      type: [String, Array, Object],
      default: '',
    },
    /** Applied to the teleported menu panel (for page-specific menu styling). */
    menuClass: {
      type: [String, Array, Object],
      default: '',
    },
    /** When true, shows a search field in the menu (auto-enabled for 4+ options unless set explicitly). */
    searchable: {
      type: Boolean,
      default: null,
    },
    searchPlaceholder: {
      type: String,
      default: 'Search...',
    },
    noResultsText: {
      type: String,
      default: 'No matches found',
    },
    allowCreate: {
      type: Boolean,
      default: false,
    },
    creating: {
      type: Boolean,
      default: false,
    },
    createPlaceholder: {
      type: String,
      default: '',
    },
    /** Stacking order for the teleported menu (raise above modals when needed). */
    menuZIndex: {
      type: Number,
      default: 10100,
    },
  },
  emits: ['update:modelValue', 'change', 'search', 'create'],
  data() {
    return {
      isOpen: false,
      activeIndex: -1,
      searchQuery: '',
      createName: '',
      menuStyle: {},
    };
  },
  computed: {
    resolvedAriaLabel() {
      return this.ariaLabel || this.label || 'Select option';
    },
    labelId() {
      return `${this.selectId}-label`;
    },
    listboxId() {
      return `${this.selectId}-listbox`;
    },
    resolvedOptions() {
      return this.options;
    },
    selectableOptions() {
      return this.resolvedOptions.filter((option) => !option.disabled);
    },
    showSearch() {
      if (this.searchable === false) return false;
      if (this.searchable === true) return this.selectableOptions.length > 0;
      return this.selectableOptions.length >= 4;
    },
    filteredOptions() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) return this.resolvedOptions;
      return this.resolvedOptions.filter((option) => {
        if (option.groupHeader) return true;
        if (option.disabled) return true;
        const label = String(option.label || '').toLowerCase();
        const value = String(option.value ?? '').toLowerCase();
        const detail = String(option.detail || '').toLowerCase();
        return label.includes(query) || value.includes(query) || detail.includes(query);
      });
    },
    filteredSelectableIndexes() {
      return this.filteredOptions
        .map((option, index) => ({ option, index }))
        .filter(({ option }) => !option.disabled);
    },
    showNoResults() {
      return (
        this.searchQuery.trim() !== '' &&
        this.filteredSelectableIndexes.length === 0 &&
        !this.showCreateFromSearch
      );
    },
    createNameTrimmed() {
      return this.createName.trim();
    },
    resolvedCreatePlaceholder() {
      if (this.createPlaceholder) return this.createPlaceholder;
      const subject = (this.label || 'item').toLowerCase();
      return `New ${subject} name`;
    },
    showCreateFromSearch() {
      if (!this.allowCreate || this.creating) return false;
      const query = this.searchQuery.trim();
      if (!query) return false;
      const lower = query.toLowerCase();
      return !this.selectableOptions.some(
        (option) => String(option.label || '').trim().toLowerCase() === lower
      );
    },
    displayLabel() {
      // Include disabled options (e.g. placeholder with value '') so empty selection
      // does not fall through to the first real option.
      const match = this.resolvedOptions.find(
        (option) => String(option.value) === String(this.modelValue)
      );
      if (match) return match.label;
      const placeholder = this.resolvedOptions.find(
        (option) => String(option.value) === ''
      );
      if (placeholder) return placeholder.label;
      const firstEnabled = this.resolvedOptions.find((option) => !option.disabled);
      return firstEnabled ? firstEnabled.label : 'Select';
    },
    selectedOption() {
      return this.resolvedOptions.find(
        (option) => String(option.value) === String(this.modelValue)
      ) || null;
    },
    selectedOptionIcon() {
      return (this.selectedOption && this.selectedOption.icon) || '';
    },
    selectedOptionTone() {
      return (this.selectedOption && this.selectedOption.tone) || 'other';
    },
    selectedOptionDetail() {
      return (this.selectedOption && this.selectedOption.detail) || '';
    },
    selectedOptionDetailTone() {
      return (this.selectedOption && this.selectedOption.detailTone) || '';
    },
    selectedOptionDetailInline() {
      return Boolean(this.selectedOption && this.selectedOption.detailInline);
    },
    selectedOptionDetailClasses() {
      const option = this.selectedOption;
      if (!option || !option.detail) return [];
      return this.optionDetailClasses(option);
    },
  },
  watch: {
    searchQuery(query) {
      this.$emit('search', query);
      this.syncActiveIndex();
    },
    filteredOptions() {
      this.syncActiveIndex();
    },
    isOpen(open) {
      if (open) {
        this.$nextTick(() => {
          this.updateMenuPosition();
          this.$nextTick(() => {
            this.updateMenuPosition();
          });
          this.attachPositionListeners();
        });
      } else {
        this.detachPositionListeners();
      }
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('keydown', this.handleDocumentKeydown);
  },
  beforeUnmount() {
    this.detachPositionListeners();
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
  },
  methods: {
    optionDetailClasses(option) {
      const classes = [];
      if (option?.detailTone) {
        classes.push(`label-dropdown__option-detail--${option.detailTone}`);
      }
      if (option?.detailPlain) {
        classes.push('label-dropdown__option-detail--plain');
      }
      return classes;
    },
    isSelected(option) {
      if (option.disabled) return false;
      return String(option.value) === String(this.modelValue);
    },
    onSearchInput(event) {
      this.searchQuery = event.target.value;
    },
    clearSearch() {
      this.searchQuery = '';
      this.focusSearchInput();
    },
    focusSearchInput() {
      this.$nextTick(() => {
        const input = this.$refs.searchInput;
        if (input) input.focus();
      });
    },
    getTriggerElement() {
      const root = this.$refs.root;
      if (!root) return null;
      return (
        root.querySelector(`#${this.selectId}`) ||
        root.querySelector('.label-dropdown__trigger')
      );
    },
    updateMenuPosition() {
      const trigger = this.getTriggerElement();
      if (!trigger || !this.isOpen) return;
      const rect = trigger.getBoundingClientRect();
      const panel = this.$refs.menuPanel;
      const menuHeight = panel?.offsetHeight || 180;
      const gap = 2;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward =
        spaceBelow < menuHeight + gap && rect.top > menuHeight + gap;

      this.menuStyle = {
        position: 'fixed',
        top: openUpward
          ? `${Math.max(8, rect.top - menuHeight - gap)}px`
          : `${rect.bottom + gap}px`,
        left: `${rect.left}px`,
        width: `${Math.max(rect.width, 140)}px`,
        zIndex: this.menuZIndex,
      };
    },
    attachPositionListeners() {
      window.addEventListener('scroll', this.updateMenuPosition, true);
      window.addEventListener('resize', this.updateMenuPosition);
    },
    detachPositionListeners() {
      window.removeEventListener('scroll', this.updateMenuPosition, true);
      window.removeEventListener('resize', this.updateMenuPosition);
    },
    toggleOpen() {
      if (this.disabled) return;
      if (this.isOpen) {
        this.close();
        return;
      }
      this.isOpen = true;
      this.searchQuery = '';
      this.syncActiveIndex();
      this.$nextTick(() => {
        this.updateMenuPosition();
        this.$nextTick(() => {
          this.updateMenuPosition();
        });
        if (this.showSearch) this.focusSearchInput();
      });
    },
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.activeIndex = -1;
      this.searchQuery = '';
      this.createName = '';
      this.menuStyle = {};
      this.detachPositionListeners();
    },
    submitCreate(name) {
      const trimmed = String(name || '').trim();
      if (!this.allowCreate || this.creating || this.disabled || !trimmed) return;
      this.$emit('create', trimmed);
      this.close();
    },
    syncActiveIndex() {
      const selectedIndex = this.filteredOptions.findIndex(
        (option) => String(option.value) === String(this.modelValue) && !option.disabled
      );
      if (selectedIndex >= 0) {
        this.activeIndex = selectedIndex;
        return;
      }
      const firstSelectable = this.filteredSelectableIndexes[0];
      this.activeIndex = firstSelectable ? firstSelectable.index : -1;
    },
    selectOption(option) {
      if (option.disabled) return;
      const value = option.value;
      this.$emit('update:modelValue', value);
      this.$emit('change', value);
      this.close();
    },
    selectActiveOption() {
      const option = this.filteredOptions[this.activeIndex];
      if (option && !option.disabled) {
        this.selectOption(option);
      }
    },
    moveActive(step) {
      const selectable = this.filteredSelectableIndexes;
      if (!selectable.length) return;

      const currentPos = selectable.findIndex(({ index }) => index === this.activeIndex);
      let nextPos = currentPos + step;
      if (currentPos === -1) {
        nextPos = step > 0 ? 0 : selectable.length - 1;
      } else if (nextPos < 0) {
        nextPos = selectable.length - 1;
      } else if (nextPos >= selectable.length) {
        nextPos = 0;
      }
      this.activeIndex = selectable[nextPos].index;
      this.scrollActiveOptionIntoView();
    },
    scrollActiveOptionIntoView() {
      this.$nextTick(() => {
        const menu = this.$refs.menuPanel;
        if (!menu) return;
        const activeEl = menu.querySelector('.label-dropdown__option--active');
        if (activeEl && typeof activeEl.scrollIntoView === 'function') {
          activeEl.scrollIntoView({ block: 'nearest' });
        }
      });
    },
    onTriggerKeydown(event) {
      if (this.disabled) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!this.isOpen) {
            this.isOpen = true;
            this.searchQuery = '';
            this.syncActiveIndex();
            this.$nextTick(() => {
              this.updateMenuPosition();
              if (this.showSearch) this.focusSearchInput();
            });
          } else {
            this.moveActive(1);
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!this.isOpen) {
            this.isOpen = true;
            this.searchQuery = '';
            this.syncActiveIndex();
            this.$nextTick(() => {
              this.updateMenuPosition();
              if (this.showSearch) this.focusSearchInput();
            });
          } else {
            this.moveActive(-1);
          }
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (this.isOpen) {
            this.selectActiveOption();
          } else {
            this.isOpen = true;
            this.searchQuery = '';
            this.syncActiveIndex();
            this.$nextTick(() => {
              this.updateMenuPosition();
              if (this.showSearch) this.focusSearchInput();
            });
          }
          break;
        case 'Escape':
          event.preventDefault();
          this.close();
          break;
        default:
          break;
      }
    },
    onSearchKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.moveActive(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.moveActive(-1);
          break;
        case 'Enter':
          event.preventDefault();
          if (this.filteredSelectableIndexes.length) {
            this.selectActiveOption();
          }
          break;
        case 'Escape':
          event.preventDefault();
          this.close();
          break;
        case 'Tab':
          this.close();
          break;
        default:
          break;
      }
    },
    handleDocumentKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    },
    handleClickOutside(event) {
      if (!this.isOpen) return;
      const root = this.$refs.root;
      const menu = this.$refs.menuPanel;
      const target = event.target;
      if (
        (root && root.contains(target)) ||
        (menu && menu.contains(target))
      ) {
        return;
      }
      this.close();
    },
  },
};
</script>

<style src="../styles/LabelDropdown.css"></style>
